// ===========================================
// Admin Absence Management Page
// ===========================================
// Admin page to view and manage all absence notifications

import { useState, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AppLocalizationProvider } from '../../components/common/AppLocalizationProvider';
import {
  Search as SearchIcon,
  EventBusy as AbsenceIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  LockOpen as ReleaseSlotIcon,
  CheckCircle as SlotReleasedIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import {
  useAllAbsences,
  useCancelAbsence,
  useReleaseAbsenteeSlot,
  useAbsenceAffectedSessions,
} from '../../hooks/useAbsenceNotification';
import {
  AbsenceNotification,
  absenceNotificationApi,
  getAbsenceReasonLabel,
  getAbsenceReasonColor,
  formatAbsenceDateRange,
  canCancelAbsence,
  canConfirmAbsence,
  getNotReleasableLabel,
} from '../../api/absenceNotification.api';

// ===========================================
// COMPONENT
// ===========================================

export default function AbsenceManagementPage() {
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [startDateFilter, setStartDateFilter] = useState<Date | null>(null);
  const [endDateFilter, setEndDateFilter] = useState<Date | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);

  // Dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [absenceToCancel, setAbsenceToCancel] = useState<AbsenceNotification | null>(null);
  const [releaseSlotDialogOpen, setReleaseSlotDialogOpen] = useState(false);
  const [absenceToRelease, setAbsenceToRelease] = useState<AbsenceNotification | null>(null);
  const [useCredit, setUseCredit] = useState(false);
  const [studentCredit, setStudentCredit] = useState<{
    termName: string;
    creditsGranted: number;
    creditsUsed: number;
    creditsRemaining: number;
  } | null>(null);
  const [creditLoading, setCreditLoading] = useState(false);
  // Per-row release-slot toggles, keyed by `${lessonId}|${lessonDate}` to keep dates unique.
  const [releaseSelections, setReleaseSelections] = useState<Record<string, boolean>>({});

  const { data: affectedSessions, isLoading: sessionsLoading } =
    useAbsenceAffectedSessions(absenceToRelease?.id ?? null);

  // Default each releasable row to ON the first time we see it.
  useEffect(() => {
    if (!affectedSessions) return;
    setReleaseSelections((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const s of affectedSessions) {
        const key = `${s.lessonId}|${s.lessonDate}`;
        if (s.releasable && !(key in next)) {
          next[key] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [affectedSessions]);

  // Queries
  const {
    data: absencesResult,
    isLoading,
    error,
    refetch,
  } = useAllAbsences({
    startDate: startDateFilter ? format(startDateFilter, 'yyyy-MM-dd') : undefined,
    endDate: endDateFilter ? format(endDateFilter, 'yyyy-MM-dd') : undefined,
    includeCancelled: showCancelled,
  });

  // Extract data from paginated result
  const absences = absencesResult?.data;

  const cancelAbsence = useCancelAbsence();
  const releaseAbsenteeSlot = useReleaseAbsenteeSlot();

  // Filter absences by search query
  const filteredAbsences = useMemo(() => {
    if (!absences) return [];
    if (!searchQuery.trim()) return absences;

    const query = searchQuery.toLowerCase();
    return absences.filter((absence) => {
      const studentName = `${absence.student.firstName} ${absence.student.lastName}`.toLowerCase();
      const submittedByName = `${absence.submittedBy.firstName} ${absence.submittedBy.lastName}`.toLowerCase();
      const reason = getAbsenceReasonLabel(absence.reason).toLowerCase();

      return (
        studentName.includes(query) ||
        submittedByName.includes(query) ||
        reason.includes(query)
      );
    });
  }, [absences, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    if (!absences) return { total: 0, active: 0, cancelled: 0 };

    const active = absences.filter((a) => !a.isCancelled).length;
    const cancelled = absences.filter((a) => a.isCancelled).length;

    return { total: absences.length, active, cancelled };
  }, [absences]);

  // Handle cancel dialog
  const handleOpenCancelDialog = (absence: AbsenceNotification) => {
    setAbsenceToCancel(absence);
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setAbsenceToCancel(null);
  };

  const handleConfirmCancel = async () => {
    if (!absenceToCancel) return;

    await cancelAbsence.mutateAsync(absenceToCancel.id);
    handleCloseCancelDialog();
  };

  // Confirm Absence handlers
  const handleOpenReleaseSlotDialog = (absence: AbsenceNotification) => {
    setAbsenceToRelease(absence);
    setUseCredit(false);
    setStudentCredit(null);
    setReleaseSelections({});
    setReleaseSlotDialogOpen(true);

    // Fetch current-term credit balance for this student
    setCreditLoading(true);
    absenceNotificationApi
      .getStudentCredits(absence.id)
      .then((credit) => setStudentCredit(credit))
      .catch(() => setStudentCredit(null))
      .finally(() => setCreditLoading(false));
  };

  const handleCloseReleaseSlotDialog = () => {
    setReleaseSlotDialogOpen(false);
    setAbsenceToRelease(null);
    setUseCredit(false);
    setStudentCredit(null);
    setReleaseSelections({});
  };

  const handleConfirmReleaseSlot = async () => {
    if (!absenceToRelease || !affectedSessions) return;

    // Per (lessonId, lessonDate) — a single absence can cover the same lesson on
    // multiple dates, and each recurrence is toggleable independently.
    const releaseSessions = affectedSessions
      .filter((s) => s.releasable && releaseSelections[`${s.lessonId}|${s.lessonDate}`])
      .map((s) => ({ lessonId: s.lessonId, lessonDate: s.lessonDate }));

    try {
      await releaseAbsenteeSlot.mutateAsync({
        id: absenceToRelease.id,
        useMakeupCredit: useCredit,
        releaseSessions,
      });
    } finally {
      handleCloseReleaseSlotDialog();
    }
  };

  // Clear filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setStartDateFilter(null);
    setEndDateFilter(null);
    setShowCancelled(false);
  };

  return (
    <AppLocalizationProvider>
      <Box>
        <PageHeader
          title="Absence Notifications"
          breadcrumbs={[
            { label: 'Dashboard', path: '/admin' },
            { label: 'Absences', path: '/admin/absences' },
          ]}
          action={
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          }
        />

        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {stats.active}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Absences
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
            <Typography variant="h4" color="text.secondary">
              {stats.cancelled}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cancelled
            </Typography>
          </Paper>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search by student or parent name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <DatePicker
              label="From Date"
              value={startDateFilter}
              onChange={setStartDateFilter}
              slotProps={{
                textField: { size: 'small', sx: { width: 160 } },
              }}
            />

            <DatePicker
              label="To Date"
              value={endDateFilter}
              onChange={setEndDateFilter}
              minDate={startDateFilter || undefined}
              slotProps={{
                textField: { size: 'small', sx: { width: 160 } },
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={showCancelled}
                  onChange={(e) => setShowCancelled(e.target.checked)}
                  size="small"
                />
              }
              label="Show cancelled"
            />

            <Button
              variant="text"
              size="small"
              onClick={handleClearFilters}
              startIcon={<FilterIcon />}
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Content */}
        <Paper>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load absence notifications.
            </Alert>
          ) : filteredAbsences.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <AbsenceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No Absences Found
              </Typography>
              <Typography color="text.secondary">
                {absences?.length === 0
                  ? 'No absence notifications have been submitted.'
                  : 'No absences match your search criteria.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Student</TableCell>
                    <TableCell>Date(s)</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Reported By</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAbsences.map((absence) => {
                    const canCancel = canCancelAbsence(absence);
                    const isCancelled = absence.isCancelled;

                    return (
                      <TableRow
                        key={absence.id}
                        sx={{
                          opacity: isCancelled ? 0.6 : 1,
                          bgcolor: isCancelled ? 'action.hover' : 'inherit',
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {absence.student.firstName} {absence.student.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatAbsenceDateRange(absence.startDate, absence.endDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Chip
                              label={getAbsenceReasonLabel(absence.reason)}
                              color={getAbsenceReasonColor(absence.reason)}
                              size="small"
                            />
                            {absence.reasonDetails && (
                              <Typography variant="caption" color="text.secondary">
                                {absence.reasonDetails}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {absence.submittedBy.firstName} {absence.submittedBy.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {absence.submittedByRole === 'PARENT' ? 'Parent' : 'Admin'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {format(new Date(absence.createdAt), 'd MMM yyyy')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(absence.createdAt), 'h:mm a')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {isCancelled ? (
                            <Chip
                              label="Cancelled"
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="Active"
                              color="success"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                            {/* Confirm Absence button — admin only, future active absences not yet confirmed */}
                            {!isCancelled && absence.slotReleased && (
                              <Tooltip title="Absence confirmed">
                                <Chip
                                  icon={<SlotReleasedIcon />}
                                  label="Confirmed"
                                  color="success"
                                  size="small"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                            {!isCancelled && !absence.slotReleased && canConfirmAbsence(absence) && (
                              <Tooltip title="Confirm absence (and optionally release time slot)">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleOpenReleaseSlotDialog(absence)}
                                  aria-label="Confirm absence"
                                >
                                  <ReleaseSlotIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Cancel button */}
                            {!isCancelled && canCancel ? (
                              <Tooltip title="Cancel this absence">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleOpenCancelDialog(absence)}
                                  aria-label="Cancel this absence"
                                >
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            ) : !isCancelled ? (
                              <Tooltip title="Cannot cancel past absences">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled
                                    aria-label="Cannot cancel past absences"
                                  >
                                    <InfoIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Confirm Absence Dialog */}
        <Dialog open={releaseSlotDialogOpen} onClose={handleCloseReleaseSlotDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Absence</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Confirm this absence. For each affected 1:1 session below you can choose
              whether to release the teacher's time slot for other students to book.
            </DialogContentText>
            {absenceToRelease && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Student:</strong>{' '}
                  {absenceToRelease.student.firstName} {absenceToRelease.student.lastName}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong>{' '}
                  {formatAbsenceDateRange(absenceToRelease.startDate, absenceToRelease.endDate)}
                </Typography>
                <Typography variant="body2">
                  <strong>Reason:</strong> {getAbsenceReasonLabel(absenceToRelease.reason)}
                </Typography>
              </Box>
            )}

            {/* Affected sessions with per-row release toggles */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Affected sessions
              </Typography>
              {sessionsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading affected sessions...
                  </Typography>
                </Box>
              ) : affectedSessions && affectedSessions.length > 0 ? (
                <Stack spacing={1.25}>
                  {affectedSessions.map((session) => {
                    const key = `${session.lessonId}|${session.lessonDate}`;
                    const dateLabel = new Date(session.lessonDate).toLocaleDateString('en-AU', {
                      weekday: 'short', day: 'numeric', month: 'short',
                    });
                    return (
                      <Box
                        key={key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'background.default',
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" noWrap>
                            <strong>{session.lessonName}</strong> · {dateLabel} · {session.startTime}–{session.endTime}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {session.teacherName} · {session.lessonTypeLabel}
                            {!session.releasable && session.notReleasableReason && (
                              <> · {getNotReleasableLabel(session.notReleasableReason)}</>
                            )}
                          </Typography>
                        </Box>
                        {session.releasable ? (
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                size="small"
                                checked={releaseSelections[key] ?? true}
                                onChange={(e) =>
                                  setReleaseSelections((prev) => ({ ...prev, [key]: e.target.checked }))
                                }
                              />
                            }
                            label={<Typography variant="caption">Release slot</Typography>}
                            labelPlacement="start"
                          />
                        ) : (
                          <Chip label="No slot to release" size="small" variant="outlined" />
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No affected sessions in this absence window.
                </Typography>
              )}
            </Box>

            {/* Makeup credit section */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Makeup Credit
              </Typography>
              {creditLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading credit balance...
                  </Typography>
                </Box>
              ) : studentCredit ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Current term ({studentCredit.termName}): {studentCredit.creditsRemaining} of{' '}
                    {studentCredit.creditsGranted} credits remaining
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useCredit}
                        onChange={(e) => setUseCredit(e.target.checked)}
                        size="small"
                        disabled={studentCredit.creditsRemaining === 0}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {studentCredit.creditsRemaining === 0
                          ? 'Use 1 makeup credit (none remaining)'
                          : 'Use 1 makeup credit for this makeup lesson'}
                        {useCredit && studentCredit.creditsRemaining > 0 && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' '}({studentCredit.creditsRemaining - 1} of{' '}
                            {studentCredit.creditsGranted} will remain)
                          </Typography>
                        )}
                      </Typography>
                    }
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No active term found — credits cannot be granted at this time.
                </Typography>
              )}
            </Box>

            <DialogContentText sx={{ mt: 2 }}>
              Teachers whose slots are released will be notified by email.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseReleaseSlotDialog}>Cancel</Button>
            <Button
              onClick={handleConfirmReleaseSlot}
              color="primary"
              variant="contained"
              disabled={releaseAbsenteeSlot.isPending || sessionsLoading || !affectedSessions}
              startIcon={<ReleaseSlotIcon />}
            >
              {releaseAbsenteeSlot.isPending ? 'Confirming...' : 'Confirm Absence'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Confirmation Dialog */}
        <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog}>
          <DialogTitle>Cancel Absence Notification?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to cancel this absence notification?
            </DialogContentText>
            {absenceToCancel && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Student:</strong>{' '}
                  {absenceToCancel.student.firstName} {absenceToCancel.student.lastName}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong>{' '}
                  {formatAbsenceDateRange(absenceToCancel.startDate, absenceToCancel.endDate)}
                </Typography>
                <Typography variant="body2">
                  <strong>Reason:</strong> {getAbsenceReasonLabel(absenceToCancel.reason)}
                </Typography>
              </Box>
            )}
            <DialogContentText sx={{ mt: 2 }}>
              The parent will be notified and future attendance records will be updated.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCancelDialog}>Keep Absence</Button>
            <Button
              onClick={handleConfirmCancel}
              color="error"
              variant="contained"
              disabled={cancelAbsence.isPending}
            >
              {cancelAbsence.isPending ? 'Cancelling...' : 'Cancel Absence'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppLocalizationProvider>
  );
}
